(function () {
  const ROOT_SEL = "#miniapp-root";
  const INIT_ATTR = "data-miniapp-init";
  const BASE_W = 800,
    BASE_H = 800;

  // Debounce
  function debounce(func, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => func.apply(this, args), wait);
    };
  }

  // Memoized image loader to avoid re-fetching assets
  const imageCache = new Map();
  function loadImage(url) {
    if (!url) return Promise.resolve(null);
    if (imageCache.has(url)) return imageCache.get(url);

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.referrerPolicy = "no-referrer";

    const promise = new Promise((res) => {
      const done = () => {
        img.onload = null;
        img.onerror = null;
        res(img);
      };
      img.onload = done;
      img.onerror = () => {
        console.warn("Image failed CORS/load:", url);
        done();
      };
    });

    img.src = url;
    if (img.complete && img.naturalWidth) {
      imageCache.set(url, Promise.resolve(img));
      return imageCache.get(url);
    }

    imageCache.set(url, promise);
    return promise;
  }

  function init(root) {
    if (!root || root.hasAttribute(INIT_ATTR)) return;

    const assetsBase =
      root.getAttribute("data-assets-base")?.replace(/\/+$/, "") || "";
    const letterVersion =
      root.getAttribute("data-letter-assets-version")?.trim() || "";
    const withLetterVersion = (url) => {
      if (!letterVersion) return url;
      const sep = url.includes("?") ? "&" : "?";
      return `${url}${sep}v=${encodeURIComponent(letterVersion)}`;
    };

    // CHANGED: bag types — remove generic birkin; add birkin25 and birkin30
    // Now supports value/label objects so template shows friendly labels.
    const purseOptions = {
      kellycut: [
        { value: "blue", label: "Blue" },
        { value: "graphite", label: "Graphite" },
        { value: "rouge", label: "Rouge" },
        { value: "vert", label: "Vert" },
        { value: "violet", label: "Violet" },
      ],
      kelly25: [
        { value: "blue", label: "Blue" },
        { value: "graphite", label: "Graphite" },
        { value: "gold", label: "Gold" },
        { value: "rouge", label: "Rouge" },
        {
          value: "mattegraphitecrocodile",
          label: "Matte Graphite Crocodile",
          requiresHardware: false,
          layout: {
            left: { x: 160, width: 170, top: BASE_H / 2 - 50, height: 45 },
            right: { x: 480, width: 170, top: BASE_H / 2 - 50, height: 45 },
          },
        },
      ],
      kelly28: [
        {
          value: "blackshinyalligator",
          label: "Black Shiny Alligator",
          requiresHardware: false,
        },
      ],
      kelly32sellier: [
        { value: "noir", label: "Noir", requiresHardware: false },
      ],
      minikelly: [
        {
          value: "goldepsom",
          label: "Gold in Epsom Leather",
          requiresHardware: false,
        },
        {
          value: "vertigoepsom",
          label: "Vert Vertigo in Epsom Leather",
          requiresHardware: false,
        },
        {
          value: "noirboxcalf",
          label: "Noir in Box Calf Leather",
          requiresHardware: false,
        },
        {
          value: "craieniloticus",
          label: "Craie in Niloticus Crocodile",
          requiresHardware: false,
        },
        {
          value: "noirniloticus",
          label: "Noir in Niloticus Crocodile",
          requiresHardware: false,
        },
      ],
      birkin25: [
        { value: "etoupe", label: "Etoupe", requiresHardware: false },
        { value: "pink", label: "Pink", requiresHardware: false },
        {
          value: "crocblack",
          label: "Black Crocodile",
          requiresHardware: false,
        },
      ], // baked hardware
      birkin30: [
        { value: "black", label: "Black", requiresHardware: false },
        { value: "gold", label: "Gold", requiresHardware: false },
        { value: "colormatic", label: "Colormatic", requiresHardware: false },
        {
          value: "crocblack",
          label: "Black Crocodile",
          requiresHardware: false,
        },
        { value: "noir", label: "Noir", requiresHardware: false },
      ], // baked hardware
      birkin35: [
        {
          value: "noirgold",
          label: "Noir with Gold Hardware",
          requiresHardware: false,
        },
        {
          value: "noirpalladium",
          label: "Noir with Palladium Hardware",
          requiresHardware: false,
        },
      ], // baked hardware
    };
    const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);
    const findPurseOption = (type, value) => {
      const options = purseOptions[type] || [];
      return options.find((opt) =>
        typeof opt === "string" ? opt === value : opt?.value === value,
      );
    };
    const hardwareIsRequired = (type, colorValue) => {
      if (!type) return true;
      const hasColor =
        colorValue !== undefined && colorValue !== null && colorValue !== "";
      const option = hasColor ? findPurseOption(type, colorValue) : null;
      if (option && typeof option === "object" && "requiresHardware" in option)
        return !!option.requiresHardware;
      // Defaults: birkin and these kelly variants ship with baked/included hardware
      if (
        type.startsWith("birkin") ||
        type === "kelly28" ||
        type === "kelly32sellier" ||
        type === "minikelly"
      )
        return false;
      return true;
    };

    // DOM
    const leftInput = root.querySelector("#leftinput");
    const leftVariant = root.querySelector("#leftvariant");
    const rightInput = root.querySelector("#rightinput");
    const rightVariant = root.querySelector("#rightvariant");
    const purseType = root.querySelector("#purseType");
    const purseVariant = root.querySelector("#pursevariant");
    const hardwareVariant = root.querySelector("#hardwarevariant");
    const previewImage = root.querySelector("#previewImage");
    const fitHint = root.querySelector("[data-fit-hint]");
    const fitChipMedium = root.querySelector('[data-fit-chip="medium"]');
    const fitChipLarge = root.querySelector('[data-fit-chip="large"]');
    const sectionEl = root.closest("section") || document;
    const mobileBar = root.querySelector("[data-mobile-bar]");
    const mobileBarLeft = root.querySelector("[data-mobile-left]");
    const mobileBarRight = root.querySelector("[data-mobile-right]");
    const variantLinkLeftEls = Array.from(
      root.querySelectorAll("[data-variant-link-left]"),
    );
    const variantLinkRightEls = Array.from(
      root.querySelectorAll("[data-variant-link-right]"),
    );
    const ctaActions = root.querySelector(".miniapp-flow__cta-actions");
    const mobileActions = root.querySelector(".miniapp-mobile-bar__actions");

    // We'll try to hide the entire hardware fieldset neatly:
    const hardwareFieldset =
      hardwareVariant?.closest(".fieldset") || hardwareVariant?.parentElement;

    const mqMobile = window.matchMedia("(max-width: 767.98px)");

    function isRootInViewport() {
      const rect = root.getBoundingClientRect();
      return rect.bottom > 120 && rect.top < window.innerHeight - 80;
    }

    function updateMobileBarSummary() {
      if (!mobileBar) return;
      const selections = orderSelections();
      const leftLabel =
        selections.left_text || selections.left_variant
          ? `Left: ${selections.left_text || "—"}${
              selections.left_variant ? " • " + selections.left_variant : ""
            }`
          : "Left: —";
      const rightLabel =
        selections.right_text || selections.right_variant
          ? `Right: ${selections.right_text || "—"}${
              selections.right_variant ? " • " + selections.right_variant : ""
            }`
          : "Right: —";
      if (mobileBarLeft) mobileBarLeft.textContent = leftLabel;
      if (mobileBarRight) mobileBarRight.textContent = rightLabel;
    }

    function syncMobileBarVisibility() {
      if (!mobileBar) return;
      const shouldShow = mqMobile.matches && isRootInViewport();
      mobileBar.classList.toggle("is-active", shouldShow);
    }

    // Canvas backing store
    const canvas = document.createElement("canvas");

    // CHANGED: positions — add birkin25 and birkin30 (start with previous birkin numbers;
    // tweak later if needed)

    // Letter zones define bounding boxes (in base canvas units) for each side.
    const BASE_LAYOUTS = {
      kellycut: {
        left: { x: 155, width: 210, top: BASE_H / 2 - 38, height: 45 },
        right: { x: 475, width: 210, top: BASE_H / 2 - 38, height: 45 },
      },
      kelly25: {
        left: { x: 210, width: 170, top: BASE_H / 2 - 125, height: 45 },
        right: { x: 490, width: 170, top: BASE_H / 2 - 125, height: 45 },
      },
      kelly28: {
        left: { x: 150, width: 190, top: BASE_H / 2 - 70, height: 45 },
        right: { x: 460, width: 190, top: BASE_H / 2 - 70, height: 45 },
      },
      kelly32sellier: {
        left: { x: 150, width: 200, top: BASE_H / 2 - 55, height: 45 },
        right: { x: 460, width: 200, top: BASE_H / 2 - 55, height: 45 },
      },
      minikelly: {
        left: { x: 140, width: 170, top: BASE_H / 2 - 15, height: 60 },
        right: { x: 495, width: 170, top: BASE_H / 2 - 15, height: 60 },
      },
      birkin25: {
        left: { x: 160, width: 200, top: BASE_H / 2 - 32, height: 48 },
        right: { x: 460, width: 200, top: BASE_H / 2 - 32, height: 48 },
      },
      birkin30: {
        left: { x: 160, width: 180, top: BASE_H / 2 - 16, height: 45 },
        right: { x: 460, width: 180, top: BASE_H / 2 - 16, height: 45 },
      },
      birkin35: {
        left: { x: 160, width: 180, top: BASE_H / 2 - 40, height: 43 },
        right: { x: 460, width: 180, top: BASE_H / 2 - 40, height: 43 },
      },
    };
    // Allow per-option layout overrides that merge onto base layout
    function resolveLayout(type, colorValue) {
      const base = BASE_LAYOUTS[type] || BASE_LAYOUTS.kellycut;
      const option = findPurseOption(type, colorValue);
      const override =
        option && typeof option === "object" ? option.layout : undefined;
      if (!override) return base;
      const mergeZone = (baseZone, overrideZone) =>
        overrideZone ? { ...baseZone, ...overrideZone } : baseZone;
      return {
        left: mergeZone(base.left, override.left),
        right: mergeZone(base.right, override.right),
      };
    }

    function setCanvasSizeForLayout() {
      const containerW = root.getBoundingClientRect().width || BASE_W;
      const isDesktop = window.matchMedia("(min-width:1024px)").matches;
      const desiredDisplayW = Math.min(
        isDesktop ? containerW * 0.58 : containerW * 0.72,
        isDesktop ? 980 : 900,
      );
      const dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 2));
      const targetW = Math.max(520, Math.round(desiredDisplayW * dpr));
      canvas.width = targetW;
      canvas.height = Math.round((BASE_H / BASE_W) * targetW);
    }

    function scaledZones(type, colorValue) {
      const layout = resolveLayout(type, colorValue);
      const scale = canvas.width / BASE_W;
      const scaleZone = (zone) => ({
        x: Math.round(zone.x * scale),
        width: Math.round(zone.width * scale),
        top: Math.round(zone.top * scale),
        height: Math.round(zone.height * scale),
      });
      return {
        left: scaleZone(layout.left),
        right: scaleZone(layout.right),
      };
    }

    // NEW: toggle hardware UI per type/color (baked or included hardware)
    function syncHardwareVisibility() {
      const type = purseType.value;
      const requiresHardware = hardwareIsRequired(type, purseVariant.value);
      if (hardwareFieldset) {
        hardwareFieldset.classList.toggle("is-hidden", !requiresHardware);
      }
      if (hardwareVariant) {
        hardwareVariant.disabled = !requiresHardware;
      }
    }

    function getMaxLetters(type) {
      if (type === "kellycut") return 5;
      if (type === "minikelly") return 2;
      return 4;
    }

    function syncLetterLimits() {
      const maxLetters = getMaxLetters(purseType.value);
      [leftInput, rightInput].forEach((input) => {
        input.maxLength = maxLetters;
        if (input.value.length > maxLetters) {
          input.value = input.value.slice(0, maxLetters);
        }
      });
    }

    function orderSelections() {
      const optText = (select) =>
        select?.selectedOptions?.[0]?.textContent?.trim() || "";
      const requiresHardware = hardwareIsRequired(
        purseType.value,
        purseVariant.value,
      );
      return {
        left_text: (leftInput.value || "").trim(),
        left_variant: optText(leftVariant),
        right_text: (rightInput.value || "").trim(),
        right_variant: optText(rightVariant),
        purse_type: optText(purseType),
        purse_type_value: purseType.value,
        purse_color: optText(purseVariant),
        purse_color_value: purseVariant.value,
        hardware: requiresHardware ? optText(hardwareVariant) : "",
        hardware_value: requiresHardware ? hardwareVariant.value : "",
      };
    }

    function updatePurseColors() {
      const selectedType = purseType.value;
      const colors = purseOptions[selectedType] || [];
      purseVariant.innerHTML = "";
      colors.forEach((color) => {
        const value = typeof color === "string" ? color : color?.value;
        const label =
          typeof color === "string"
            ? capitalize(color)
            : color?.label || capitalize(value || "");
        if (!value) return;
        const o = document.createElement("option");
        o.value = value;
        o.textContent = label;
        purseVariant.appendChild(o);
      });
      if (colors.length > 0) {
        const firstValue =
          typeof colors[0] === "string" ? colors[0] : colors[0]?.value;
        if (firstValue) purseVariant.value = firstValue;
      }

      syncHardwareVisibility(); // NEW: hide/show when type changes
      syncLetterLimits(); // enforce max letters per purse type
      updateFitHint();
      updateMobileBarSummary();
      debouncedUpdate();
    }

    function setChipState(el, state) {
      if (!el) return;
      el.classList.toggle("is-best", state === "best");
      el.classList.toggle("is-compat", state === "compat");
    }

    function updateFitHint() {
      if (!fitHint) return;
      const type = purseType.value;
      const best = new Set([
        "kelly28",
        "kelly32sellier",
        "birkin30",
        "birkin35",
      ]);
      const mediumPref = new Set([
        "kellycut",
        "kelly25",
        "minikelly",
        "birkin25",
      ]);

      if (best.has(type)) {
        setChipState(fitChipLarge, "best");
        setChipState(fitChipMedium, "");
        if (fitChipMedium) fitChipMedium.hidden = true;
      } else if (mediumPref.has(type)) {
        setChipState(fitChipMedium, "best");
        setChipState(fitChipLarge, "compat");
        if (fitChipMedium) fitChipMedium.hidden = false;
      } else {
        setChipState(fitChipMedium, "compat");
        setChipState(fitChipLarge, "compat");
        if (fitChipMedium) fitChipMedium.hidden = false;
      }
    }

    function drawTextImages(ctx, text, variant, zone) {
      const sanitized = (text || "").replace(/[^A-Z]/g, "");
      if (!sanitized) return Promise.resolve();

      const letters = sanitized.split("").map((c) => ({
        c,
        url: withLetterVersion(`${assetsBase}/${variant}/${c}.png`),
      }));
      const letterHeight = zone.height;
      const letterSpacing = Math.max(1, Math.round(letterHeight * 0.05));
      const spaceWidth = Math.round(letterHeight * 0.45);

      return Promise.all(letters.map(({ url }) => loadImage(url))).then(
        (images) => {
          const metrics = images.map((img) => {
            const ar =
              img && img.naturalWidth && img.naturalHeight
                ? img.naturalWidth / img.naturalHeight
                : img && img.width && img.height
                  ? img.width / img.height
                  : null;
            return ar ? letterHeight * ar : spaceWidth;
          });

          const totalWidth = metrics.reduce((sum, width, idx) => {
            const gap = idx < metrics.length - 1 ? letterSpacing : 0;
            return sum + width + gap;
          }, 0);
          const maxX = zone.x + zone.width;

          let startX = zone.x;
          if (totalWidth < zone.width) {
            startX += Math.round((zone.width - totalWidth) / 2);
          }
          // Keep overflow centered as best as possible while staying within bounds
          if (startX < zone.x) startX = zone.x;
          if (startX + totalWidth > maxX)
            startX = Math.max(zone.x, maxX - totalWidth);

          let x = startX;
          letters.forEach((_, idx) => {
            const img = images[idx];
            const width = metrics[idx] || spaceWidth;
            if (img)
              ctx.drawImage(
                img,
                x,
                zone.top - letterHeight / 2,
                width,
                letterHeight,
              );
            x += width;
            if (idx < letters.length - 1) x += letterSpacing;
          });
        },
      );
    }

    function updatePreview() {
      setCanvasSizeForLayout();

      const leftText = leftInput.value.toUpperCase();
      const rightText = rightInput.value.toUpperCase();

      const off = document.createElement("canvas");
      off.width = canvas.width;
      off.height = canvas.height;
      const offCtx = off.getContext("2d");

      const type = purseType.value;
      const purseUrl = `${assetsBase}/bag_${type}/${purseVariant.value}.png`;

      // Only load hardware overlay when required for this color
      const needsHardware = hardwareIsRequired(type, purseVariant.value);
      const hardwareUrl = needsHardware
        ? `${assetsBase}/hardware_${type}/${hardwareVariant.value}.png`
        : null;

      Promise.all([
        loadImage(purseUrl),
        needsHardware ? loadImage(hardwareUrl) : Promise.resolve(null),
      ]).then(([purseImg, hardwareImg]) => {
        if (!purseImg) return;
        const w = off.width;
        const hPurse =
          purseImg && purseImg.width
            ? (purseImg.height / purseImg.width) * w
            : off.height;
        offCtx.clearRect(0, 0, off.width, off.height);
        offCtx.fillStyle = "rgb(250, 250, 250)";
        offCtx.fillRect(0, 0, off.width, off.height);
        offCtx.drawImage(purseImg, (off.width - w) / 2, 0, w, hPurse);

        if (needsHardware && hardwareImg) {
          const hHardware =
            hardwareImg && hardwareImg.width
              ? (hardwareImg.height / hardwareImg.width) * w
              : hPurse;
          offCtx.drawImage(
            hardwareImg,
            off.height - hHardware,
            (off.width - w) / 2,
            w,
            hHardware,
          );
        }

        const zones = scaledZones(type, purseVariant.value);
        Promise.all([
          drawTextImages(offCtx, leftText, leftVariant.value, zones.left),
          drawTextImages(offCtx, rightText, rightVariant.value, zones.right),
        ]).then(() => {
          try {
            const dataUrl = off.toDataURL("image/jpeg", 0.85);
            previewImage.src = dataUrl;
            updateMobileBarSummary();
            updateVariantLinks();
          } catch (e) {
            console.error("Canvas toDataURL failed (CORS?):", e);
          }
        });
      });
    }

    const variantUrlMap = {
      yellow_gold_diamonds:
        root.dataset.variantUrlYellowGoldDiamonds?.trim() || "",
      white_gold_diamonds:
        root.dataset.variantUrlWhiteGoldDiamonds?.trim() || "",
      rose_gold_diamonds: root.dataset.variantUrlRoseGoldDiamonds?.trim() || "",
      yellow_gold_plain: root.dataset.variantUrlYellowGoldPlain?.trim() || "",
      white_gold_plain: root.dataset.variantUrlWhiteGoldPlain?.trim() || "",
      rose_gold_plain: root.dataset.variantUrlRoseGoldPlain?.trim() || "",
    };
    const variantUrlLargeMap = {
      yellow_gold_diamonds:
        root.dataset.variantUrlYellowGoldDiamondsLarge?.trim() || "",
      white_gold_diamonds:
        root.dataset.variantUrlWhiteGoldDiamondsLarge?.trim() || "",
      rose_gold_diamonds:
        root.dataset.variantUrlRoseGoldDiamondsLarge?.trim() || "",
      yellow_gold_plain:
        root.dataset.variantUrlYellowGoldPlainLarge?.trim() || "",
      white_gold_plain:
        root.dataset.variantUrlWhiteGoldPlainLarge?.trim() || "",
      rose_gold_plain: root.dataset.variantUrlRoseGoldPlainLarge?.trim() || "",
    };
    const variantLabelMap = {
      yellow_gold_diamonds:
        root.dataset.variantLabelYellowGoldDiamonds?.trim() || "",
      white_gold_diamonds:
        root.dataset.variantLabelWhiteGoldDiamonds?.trim() || "",
      rose_gold_diamonds:
        root.dataset.variantLabelRoseGoldDiamonds?.trim() || "",
      yellow_gold_plain: root.dataset.variantLabelYellowGoldPlain?.trim() || "",
      white_gold_plain: root.dataset.variantLabelWhiteGoldPlain?.trim() || "",
      rose_gold_plain: root.dataset.variantLabelRoseGoldPlain?.trim() || "",
    };
    const getBagSize = (type) => {
      const large = new Set([
        "kelly28",
        "kelly32sellier",
        "birkin30",
        "birkin35",
      ]);
      const medium = new Set(["kellycut", "kelly25", "minikelly", "birkin25"]);
      if (large.has(type)) return "large";
      if (medium.has(type)) return "medium";
      return "medium";
    };
    const getVariantUrl = (variantValue) => {
      if (!variantValue) return "";
      const size = getBagSize(purseType.value);
      const map = size === "large" ? variantUrlLargeMap : variantUrlMap;
      return map[variantValue] || "";
    };
    const getVariantLabel = (variantValue, fallback) => {
      if (!variantValue) return fallback;
      return variantLabelMap[variantValue] || fallback;
    };

    function setLinkState(els, url, label, variantValue) {
      els.forEach((el) => {
        if (!el) return;
        if (url) {
          el.hidden = false;
          el.setAttribute("href", url);
          const letterUrl = variantValue
            ? withLetterVersion(`${assetsBase}/${variantValue}/A.png`)
            : "";
          el.innerHTML = "";
          if (letterUrl) {
            const img = document.createElement("img");
            img.src = letterUrl;
            img.alt = "";
            img.loading = "lazy";
            img.decoding = "async";
            img.className = "miniapp-cta-letter";
            el.appendChild(img);
          }
          const span = document.createElement("span");
          span.className = "miniapp-cta-label";
          span.textContent = label || "";
          el.appendChild(span);
          el.style.display = "";
          el.removeAttribute("aria-hidden");
        } else {
          el.hidden = true;
          el.removeAttribute("href");
          el.textContent = "";
          el.style.display = "none";
          el.setAttribute("aria-hidden", "true");
        }
      });
    }

    function updateActionLayout(container) {
      if (!container) return;
      const links = Array.from(container.querySelectorAll("a"));
      const visible = links.filter((el) => el && !el.hidden);
      const count = visible.length;
      container.dataset.ctaCount = count ? String(count) : "0";
    }

    function updateVariantLinks() {
      const leftValue = leftVariant?.value || "";
      const rightValue = rightVariant?.value || "";
      const leftLabel =
        leftVariant?.selectedOptions?.[0]?.textContent?.trim() ||
        leftVariant?.options?.[leftVariant.selectedIndex]?.text ||
        "";
      const rightLabel =
        rightVariant?.selectedOptions?.[0]?.textContent?.trim() ||
        rightVariant?.options?.[rightVariant.selectedIndex]?.text ||
        "";
      const leftUrl = getVariantUrl(leftValue);
      const rightUrl = getVariantUrl(rightValue);
      const leftButtonLabel =
        getVariantLabel(leftValue, "").trim() ||
        (leftLabel ? `Shop ${leftLabel}` : "Shop selected charm");
      const rightButtonLabel =
        getVariantLabel(rightValue, "").trim() ||
        (rightLabel ? `Shop ${rightLabel}` : "Shop selected charm");

      const bothSameVariant =
        leftValue && rightValue && leftValue === rightValue;
      const hasLeft = !!leftUrl;
      const hasRight = !!rightUrl;

      if (bothSameVariant) {
        if (leftUrl) {
          setLinkState(variantLinkLeftEls, leftUrl, leftButtonLabel, leftValue);
          setLinkState(variantLinkRightEls, "", "");
        } else {
          setLinkState(variantLinkLeftEls, "", "");
          setLinkState(variantLinkRightEls, "", "");
        }
      } else if (hasLeft && hasRight) {
        setLinkState(variantLinkLeftEls, leftUrl, leftButtonLabel, leftValue);
        setLinkState(
          variantLinkRightEls,
          rightUrl,
          rightButtonLabel,
          rightValue,
        );
      } else if (hasLeft || hasRight) {
        const url = hasLeft ? leftUrl : rightUrl;
        const label = hasLeft ? leftButtonLabel : rightButtonLabel;
        const variantValue = hasLeft ? leftValue : rightValue;
        setLinkState(variantLinkLeftEls, url, label, variantValue);
        setLinkState(variantLinkRightEls, "", "");
      } else {
        setLinkState(variantLinkLeftEls, "", "");
        setLinkState(variantLinkRightEls, "", "");
      }

      updateActionLayout(ctaActions);
      updateActionLayout(mobileActions);
    }

    const debouncedUpdate = debounce(updatePreview, 120);

    function initMiniappCarousel(root) {
      const carousels = root.querySelectorAll("[data-carousel]");
      const destroyers = [];

      carousels.forEach((wrap) => {
        const viewport = wrap.querySelector("[data-carousel-viewport]");
        const track = wrap.querySelector("[data-carousel-track]");
        const prevBtn = wrap.querySelector("[data-carousel-prev]");
        const nextBtn = wrap.querySelector("[data-carousel-next]");
        if (!viewport || !track || !prevBtn || !nextBtn) return;

        function pageWidth() {
          return viewport.clientWidth;
        }
        function maxScroll() {
          return track.scrollWidth - viewport.clientWidth;
        }
        function updateDisabled() {
          const x = viewport.scrollLeft;
          prevBtn.disabled = x <= 0;
          nextBtn.disabled = x >= maxScroll() - 2;
        }

        function onPrev() {
          viewport.scrollBy({ left: -pageWidth(), behavior: "smooth" });
        }
        function onNext() {
          viewport.scrollBy({ left: pageWidth(), behavior: "smooth" });
        }

        prevBtn.addEventListener("click", onPrev);
        nextBtn.addEventListener("click", onNext);
        viewport.addEventListener("scroll", updateDisabled);
        window.addEventListener("resize", updateDisabled);

        // initial state
        updateDisabled();

        destroyers.push(() => {
          prevBtn.removeEventListener("click", onPrev);
          nextBtn.removeEventListener("click", onNext);
          viewport.removeEventListener("scroll", updateDisabled);
          window.removeEventListener("resize", updateDisabled);
        });
      });

      return () =>
        destroyers.forEach((fn) => {
          try {
            fn();
          } catch (_) {}
        });
    }

    // Events
    const onLeftInput = () => {
      leftInput.value = leftInput.value.replace(/[^A-Z]/gi, "").toUpperCase();
      const maxLetters = getMaxLetters(purseType.value);
      if (leftInput.value.length > maxLetters) {
        leftInput.value = leftInput.value.slice(0, maxLetters);
      }
      updateMobileBarSummary();
      updateVariantLinks();
      debouncedUpdate();
    };
    const onRightInput = () => {
      rightInput.value = rightInput.value.replace(/[^A-Z]/gi, "").toUpperCase();
      const maxLetters = getMaxLetters(purseType.value);
      if (rightInput.value.length > maxLetters) {
        rightInput.value = rightInput.value.slice(0, maxLetters);
      }
      updateMobileBarSummary();
      updateVariantLinks();
      debouncedUpdate();
    };

    leftInput.addEventListener("input", onLeftInput);
    rightInput.addEventListener("input", onRightInput);
    const onConfigChange = () => {
      syncHardwareVisibility();
      updateMobileBarSummary();
      updateVariantLinks();
      debouncedUpdate();
      syncMobileBarVisibility();
    };
    leftVariant.addEventListener("change", onConfigChange);
    rightVariant.addEventListener("change", onConfigChange);
    purseVariant.addEventListener("change", onConfigChange);
    hardwareVariant.addEventListener("change", onConfigChange);
    purseType.addEventListener("change", updatePurseColors);

    // Resize -> recalc + redraw
    const onResize = debounce(() => {
      updatePreview();
      syncMobileBarVisibility();
    }, 150);
    window.addEventListener("resize", onResize);
    const onViewportChange = debounce(() => {
      syncMobileBarVisibility();
    }, 80);
    window.addEventListener("scroll", onViewportChange, { passive: true });

    // Kick off
    updatePurseColors(); // populates variants + hides hardware for birkin + syncs inputs
    const destroyCarousel = initMiniappCarousel(sectionEl);

    updateMobileBarSummary();
    updateVariantLinks();

    // Teardown
    root._miniappDestroyers = [
      () => leftInput.removeEventListener("input", onLeftInput),
      () => rightInput.removeEventListener("input", onRightInput),
      () => leftVariant.removeEventListener("change", onConfigChange),
      () => rightVariant.removeEventListener("change", onConfigChange),
      () => purseVariant.removeEventListener("change", onConfigChange),
      () => hardwareVariant.removeEventListener("change", onConfigChange),
      () => purseType.removeEventListener("change", updatePurseColors),
      () => window.removeEventListener("resize", onResize),
      () => window.removeEventListener("scroll", onViewportChange),
      () => {
        try {
          destroyCarousel && destroyCarousel();
        } catch (_) {}
      },
    ];

    root.setAttribute(INIT_ATTR, "");
  }

  function destroy(root) {
    if (!root || !root.hasAttribute(INIT_ATTR)) return;
    (root._miniappDestroyers || []).forEach((fn) => {
      try {
        fn();
      } catch (_) {}
    });
    root._miniappDestroyers = [];
    root.removeAttribute(INIT_ATTR);
  }

  function initAll() {
    document.querySelectorAll(ROOT_SEL).forEach(init);
  }
  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", initAll);
  else initAll();

  // Theme editor lifecycle
  document.addEventListener("shopify:section:load", (e) => {
    const root = e.target?.querySelector(ROOT_SEL);
    if (root) init(root);
  });
  document.addEventListener("shopify:section:unload", (e) => {
    const root = e.target?.querySelector(ROOT_SEL);
    if (root) destroy(root);
  });
})();
